// Simplified version of https://github.com/mui/material-ui/blob/master/packages/mui-material/src/usePagination/usePagination.js
/** @param {import("./types.d").default} props */

const usePagination = (props = { page: 1 }) => {
  const { boundaryCount = 1, count = 1, hideNextButton = false, hidePrevButton = false, page, siblingCount = 1 } = props

  const range = (start, end) => {
    const length = end - start + 1
    return Array.from({ length }, (_, i) => start + i)
  }

  const startPages = range(1, Math.min(boundaryCount, count))
  const endPages = range(Math.max(count - boundaryCount + 1, boundaryCount + 1), count)

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      count - boundaryCount - siblingCount * 2 - 1
    ),
    // Greater than startPages
    boundaryCount + 2
  )

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : count - 1
  )

  // Basic list of items to render
  // e.g. itemList = ['previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next']
  const itemList = [
    ...(hidePrevButton ? [] : ['previous']),
    ...startPages,

    // Start ellipsis

    ...(siblingsStart > boundaryCount + 2
      ? ['start-ellipsis']
      : boundaryCount + 1 < count - boundaryCount
      ? [boundaryCount + 1]
      : []),

    // Sibling pages
    ...range(siblingsStart, siblingsEnd),

    // End ellipsis

    ...(siblingsEnd < count - boundaryCount - 1
      ? ['end-ellipsis']
      : count - boundaryCount > boundaryCount
      ? [count - boundaryCount]
      : []),

    ...endPages,
    ...(hideNextButton ? [] : ['next']),
  ]

  // Map the button type to its page number
  const buttonPage = type => {
    switch (type) {
      case 'previous':
        return page - 1
      case 'next':
        return page + 1
      default:
        return 1
    }
  }

  // Convert the basic item list to PaginationItem props objects
  const items = itemList.map(item => {
    return typeof item === 'number'
      ? {
          type: 'page',
          page: item,
          selected: item === page,
          'aria-current': item === page ? 'true' : undefined,
        }
      : {
          type: item,
          page: buttonPage(item),
          selected: false,
          disabled:
            item.indexOf('ellipsis') !== -1 || (item === 'next' && page >= count) || (item === 'previous' && page <= 1),
        }
  })

  return {
    items,
  }
}

export default usePagination
